import { Dispatch, SetStateAction, useMemo } from 'react';
import { Col, Row } from 'antd';
import { useFormik } from 'formik';
import { capitalize } from 'lodash';
import SearchBar from 'components/new-ui/base/searchbar/SearchBar';
import FormSelect, { SelectOption } from 'components/new-ui/composed/formSelect/FormSelect';
import CloseButton from 'components/new-ui/composed/modal/filters-modal/CloseButton';
import FiltersModal from 'components/new-ui/composed/modal/filters-modal/FiltersModal';
import { DeclarationCountry, declarationCountryLabels } from 'store/declarations/enums/common/declaration-country';
import { TeamStatuses } from 'store/teams/types';

type Props = {
    open: boolean;
    market: DeclarationCountry | undefined;
    queryInputVal: string;
    status: TeamStatuses | undefined;
    onClose: () => void;
    clearAll: () => void;
    setQueryInputVal: Dispatch<SetStateAction<string>>;
    setPage: Dispatch<SetStateAction<number>>;
    setMarket: Dispatch<SetStateAction<DeclarationCountry | undefined>>;
    setStatus: Dispatch<SetStateAction<TeamStatuses | undefined>>;
};

type FormValues = {
    query: string;
    market: DeclarationCountry | null;
    status: TeamStatuses | undefined;
};

const TEAM_STATUS_OPTIONS: SelectOption[] = [
    {
        id: TeamStatuses.ACTIVE,
        value: 'Active',
    },
    {
        id: TeamStatuses.INACTIVE,
        value: 'Inactive',
    },
    {
        id: TeamStatuses.DRAFT,
        value: 'Draft',
    },
];

const FilterTeamsModal = ({
    open,
    queryInputVal,
    market,
    status,
    onClose,
    setQueryInputVal,
    clearAll,
    setMarket,
    setPage,
    setStatus,
}: Props) => {
    const initialValues: FormValues = useMemo(
        () => ({
            query: queryInputVal ?? '',
            market: market ?? null,
            status,
        }),
        [queryInputVal, market, status]
    );
    const formik = useFormik<FormValues>({
        initialValues,
        enableReinitialize: true,
        onSubmit: () => {},
    });

    const handleClearFilters = () => {
        formik.setFieldValue('market', undefined);
        formik.setFieldValue('status', undefined);
        clearAll();
    };

    const handleClearMarket = () => {
        formik.setFieldValue('market', undefined);
        setMarket(undefined);
    };

    const handleStatusChange = (val?: TeamStatuses) => {
        formik.setFieldValue('status', val, false);
        setStatus(val);
        setPage(0);
    };

    const handleClearStatus = () => {
        formik.setFieldValue('status', undefined);
        setStatus(undefined);
    };

    const hasNoFiltersApplied = useMemo(() => {
        const hasMarket = !!formik.values.market;
        const hasQuery = !!formik.values.query;
        const hasStatus = !!formik.values.status;
        return !(hasMarket || hasQuery || hasStatus);
    }, [formik.values]);

    return (
        <FiltersModal
            header={
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 16 }}>
                    <SearchBar
                        onChange={(val) => formik.setFieldValue('query', val, false)}
                        inputPlaceholder="Search"
                        style={{ width: '100%' }}
                        value={queryInputVal}
                        onSearch={(val) => {
                            setQueryInputVal(val);
                            setPage(0);
                        }}
                        onClear={() => {
                            setQueryInputVal('');
                            setPage(0);
                        }}
                    />
                    <CloseButton onClose={onClose} />
                </div>
            }
            destroyOnClose
            open={open}
            width={640}
            onClearFilters={handleClearFilters}
            onClose={onClose}
            onSubmit={onClose}
            clearButtonDisabled={hasNoFiltersApplied}
        >
            <Row gutter={40}>
                <Col span={12}>
                    <label htmlFor="market">Market</label>
                    <FormSelect
                        hideKeys
                        placeholder="Select market"
                        value={market}
                        fieldProps={formik.getFieldProps('market')}
                        selectOptions={declarationCountryLabels.map((el) => ({
                            id: el.id,
                            value: capitalize(el.value),
                        }))}
                        onChange={(val) => setMarket(val)}
                        onClear={handleClearMarket}
                    />
                </Col>
                <Col span={12}>
                    <label htmlFor="status">Status</label>
                    <FormSelect
                        hideKeys
                        placeholder="Select status"
                        value={status}
                        fieldProps={formik.getFieldProps('status')}
                        selectOptions={TEAM_STATUS_OPTIONS}
                        onChange={handleStatusChange}
                        onClear={handleClearStatus}
                    />
                </Col>
            </Row>
        </FiltersModal>
    );
};

export default FilterTeamsModal;
